import {
  GltfDirectBuildError,
  GltfReferenceOutOfBoundsError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import {
  getGltfComponentByteSize,
  getGltfComponentCount,
} from '@/plugins/mesh/loaders/gltfLowMemory/accessorLayout'
import type {
  GltfAccessorReader,
  GltfBufferSource,
  GltfTaskScheduler,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { DEFAULT_GLTF_LOADING_OPTIONS } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import {
  GltfYieldController,
  MacrotaskGltfScheduler,
} from '@/plugins/mesh/loaders/gltfLowMemory/MacrotaskGltfScheduler'
import type {
  GltfAccessorArray,
  GltfAccessorData,
  GltfComponentType,
  GltfManifestDocument,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import type { GltfTaskYieldOptions } from '@/plugins/mesh/types'

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function createAccessorArray(componentType: GltfComponentType, length: number): GltfAccessorArray {
  switch (componentType) {
    case 5120:
      return new Int8Array(length)
    case 5121:
      return new Uint8Array(length)
    case 5122:
      return new Int16Array(length)
    case 5123:
      return new Uint16Array(length)
    case 5125:
      return new Uint32Array(length)
    case 5126:
      return new Float32Array(length)
  }
}

function readComponent(
  view: DataView,
  byteOffset: number,
  componentType: GltfComponentType
): number {
  switch (componentType) {
    case 5120:
      return view.getInt8(byteOffset)
    case 5121:
      return view.getUint8(byteOffset)
    case 5122:
      return view.getInt16(byteOffset, true)
    case 5123:
      return view.getUint16(byteOffset, true)
    case 5125:
      return view.getUint32(byteOffset, true)
    case 5126:
      return view.getFloat32(byteOffset, true)
  }
}

/** 支持双重 offset、stride 和 normalized 语义的标准 accessor reader。 */
export class DefaultGltfAccessorReader implements GltfAccessorReader {
  private readonly cache = new WeakMap<
    GltfManifestDocument,
    Map<number, Promise<GltfAccessorData>>
  >()

  constructor(
    private readonly scheduler: GltfTaskScheduler = new MacrotaskGltfScheduler(),
    private readonly taskYield: GltfTaskYieldOptions = DEFAULT_GLTF_LOADING_OPTIONS.taskYield
  ) {}

  read(
    document: GltfManifestDocument,
    accessorIndex: number,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<GltfAccessorData> {
    const documentCache = this.cache.get(document) ?? new Map()
    this.cache.set(document, documentCache)
    const cached = documentCache.get(accessorIndex)
    if (cached) return cached

    const promise = this.readUncached(document, accessorIndex, sources, signal)
    documentCache.set(accessorIndex, promise)
    promise.catch(() => documentCache.delete(accessorIndex))
    return promise
  }

  release(document: GltfManifestDocument, accessorIndex: number): void {
    this.cache.get(document)?.delete(accessorIndex)
  }

  private async readUncached(
    document: GltfManifestDocument,
    accessorIndex: number,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<GltfAccessorData> {
    const { manifest, source } = document
    throwIfCancelled(signal, source)
    const accessor = manifest.accessors?.[accessorIndex]
    if (!accessor) {
      throw new GltfReferenceOutOfBoundsError(source, `accessors[${accessorIndex}]`)
    }
    if (accessor.sparse) {
      throw new GltfDirectBuildError(source, `accessors[${accessorIndex}] 是 sparse accessor`)
    }
    const componentCount = getGltfComponentCount(accessor.type)
    const componentByteSize = getGltfComponentByteSize(accessor.componentType)
    const elementByteSize = componentCount * componentByteSize
    if (accessor.bufferView === undefined) {
      return {
        layout: {
          componentType: accessor.componentType,
          componentCount,
          componentByteSize,
          elementByteSize,
          count: accessor.count,
          normalized: accessor.normalized ?? false,
          byteOffset: 0,
          byteStride: elementByteSize,
        },
        array: createAccessorArray(accessor.componentType, accessor.count * componentCount),
      }
    }
    const bufferView = manifest.bufferViews?.[accessor.bufferView]
    if (!bufferView) {
      throw new GltfReferenceOutOfBoundsError(source, `accessors[${accessorIndex}].bufferView`)
    }
    const bufferSource = sources[bufferView.buffer]
    if (!bufferSource) {
      throw new GltfReferenceOutOfBoundsError(source, `buffers[${bufferView.buffer}] source`)
    }

    const byteStride = bufferView.byteStride ?? elementByteSize
    const accessorByteOffset = accessor.byteOffset ?? 0
    const byteOffset = (bufferView.byteOffset ?? 0) + accessorByteOffset
    const byteLength =
      accessor.count === 0 ? 0 : (accessor.count - 1) * byteStride + elementByteSize
    const bytes = await bufferSource.read(byteOffset, byteLength, signal)
    throwIfCancelled(signal, source)

    const array = createAccessorArray(accessor.componentType, accessor.count * componentCount)
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const yieldController = new GltfYieldController(this.scheduler, this.taskYield)
    for (let elementIndex = 0; elementIndex < accessor.count; elementIndex += 1) {
      const sourceElementOffset = elementIndex * byteStride
      const targetElementOffset = elementIndex * componentCount
      for (let componentIndex = 0; componentIndex < componentCount; componentIndex += 1) {
        array[targetElementOffset + componentIndex] = readComponent(
          view,
          sourceElementOffset + componentIndex * componentByteSize,
          accessor.componentType
        )
      }
      const pendingYield = yieldController.consume(elementByteSize, 1, signal)
      if (pendingYield) await pendingYield
    }

    return {
      layout: {
        componentType: accessor.componentType,
        componentCount,
        componentByteSize,
        elementByteSize,
        count: accessor.count,
        normalized: accessor.normalized ?? false,
        byteOffset,
        byteStride,
      },
      array,
    }
  }
}
